import { Flex } from '@/components/Flex'
import { MenuItemProps } from '@/components/layouts/Sidebar'
import { Typography } from '@/components/Typography'
import { QuickNavigationHeapEvents } from '@/heap/heapEvents'
import { trackHeapEvent } from '@/heap/trackHeapEvent'
import { useGetStoreLocations } from '@/queries/storeLocations/useGetStoreLocations'
import { formatAddressInline } from '@/utils/functions/addresses/formatAddressInline'
import { ShopOutlined } from '@ant-design/icons'
import { Command } from 'cmdk'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './styles.module.scss'

export type QuickNavigationOpenSource = 'button' | 'keyboard'

export const QUICK_NAVIGATION_TEST_ID = 'quick-navigation-test-id'

export interface QuickNavigationProps {
  source: QuickNavigationOpenSource | null
  navigationItems: MenuItemProps[]
  navigate: (path: string) => void
  open: boolean
  setOpen: (open: QuickNavigationOpenSource | null) => void
}

export const QuickNavigation = ({
  source,
  navigationItems,
  navigate,
  open,
  setOpen,
}: QuickNavigationProps) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')

  const { data: storeLocations, isLoading } = useGetStoreLocations({ enabled: search.length > 0 })

  const commandListRef = useRef<HTMLDivElement>(null)

  const storeLocationOptions =
    storeLocations?.map((storeLocation) => ({
      value: storeLocation.id,
      address: formatAddressInline({
        address: {
          addressLocality: storeLocation.addressLocality,
          addressRegion: storeLocation.addressRegion,
          postalCode: storeLocation.postalCode,
          streetAddress: storeLocation.streetAddress,
          streetAddressDetail: storeLocation.streetAddressDetail,
        },
        showZipCode: true,
        formattedCountry: storeLocation.country.toUpperCase(),
      }),
      label: storeLocation.title,
      storeCode: storeLocation.storeCode,
    })) || []

  const handleInputValueChange = (value: string) => {
    setSearch(value)
    commandListRef.current?.scrollTo({ top: 0 })
  }

  const handleSelect = (path: string) => {
    trackHeapEvent(QuickNavigationHeapEvents.itemClicked, {
      path,
    })
    navigate(path)
    setSearch('')
    setOpen(null)
  }

  useEffect(() => {
    if (!open) return
    trackHeapEvent(QuickNavigationHeapEvents.opened, {
      source,
    })
  }, [open, source])

  return (
    <Command.Dialog
      open={open}
      onOpenChange={(open) => setOpen(open ? source : null)}
      title={t('components.quickNavigation.title')}
      className={styles.dialog}
      label={t('components.quickNavigation.placeholder')}
      modal
      aria-describedby="quick-navigation-description"
      aria-description={t('components.quickNavigation.description')}
      data-testid={QUICK_NAVIGATION_TEST_ID}
    >
      <div className={styles.palette}>
        <Command.Input
          placeholder={t('components.quickNavigation.placeholder')}
          value={search}
          onValueChange={handleInputValueChange}
        />
        <Command.List ref={commandListRef}>
          <Command.Empty>{t('components.quickNavigation.noResultsFound')}</Command.Empty>

          <Command.Group heading={t('components.quickNavigation.pages')}>
            {navigationItems.map((item) => (
              <Command.Item
                key={item.key}
                onSelect={() => handleSelect(item.path || '')}
                keywords={['pages', 'page']}
              >
                <div>{item.icon}</div>
                <Typography.Text>{item.quickNavTitle}</Typography.Text>
              </Command.Item>
            ))}
          </Command.Group>

          <Command.Group heading={t('components.quickNavigation.locations')}>
            {isLoading ? (
              <Command.Item disabled>{t('components.quickNavigation.loading')}</Command.Item>
            ) : search.length === 0 ? (
              <Command.Item disabled>
                {t('components.quickNavigation.startTypingLocations')}
              </Command.Item>
            ) : (
              storeLocationOptions.map((option) => (
                <Command.Item
                  key={option.value}
                  id={option.value}
                  value={`${option.value}-${option.label}-${option.address}-${option.storeCode}`}
                  onSelect={() => handleSelect(`/locations/${option.value}`)}
                  keywords={['store', 'location']}
                >
                  <div>
                    <ShopOutlined size={14} />
                  </div>
                  <Flex vertical>
                    <Typography.Text>{option.label}</Typography.Text>
                    <Typography.Text type="secondary">{option.address}</Typography.Text>
                    {option.storeCode && (
                      <span className={styles.itemExtraInfo}>{option.storeCode}</span>
                    )}
                  </Flex>
                </Command.Item>
              ))
            )}
          </Command.Group>
        </Command.List>
      </div>
    </Command.Dialog>
  )
}
